import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProcedureRevisionsForPart } from "../../../redux/procedureRevisionSlice";
import { getBom } from "../../../redux/partSlice";

/**
 * Custom hook to fetch and manage preview-related data
 * Simplified version of useDocumentData for preview purposes
 * Does not fetch user lists since preview mode doesn't need approval functionality
 *
 * @param {string} partId - The part ID
 * @param {string} revisionId - The revision ID
 * @returns {Object} Preview-related data from Redux store with loading states
 */
const usePreviewData = (partId, revisionId) => {
  const dispatch = useDispatch();

  // Loading states for different sections
  const [loading, setLoading] = useState({
    revisionData: true,
    trainingLogs: true,
    bomTable: true,
    appSetup: true,
  });

  // Select data from Redux store
  const { revisionData, trainingLogs } = useSelector(
    (state) => state.procedureRevision
  );

  const { bomTable } = useSelector((state) => state.parts);

  // Fetch required data on mount or when dependencies change
  useEffect(() => {
    if (partId && revisionId) {
      // Reset loading states
      setLoading({
        revisionData: true,
        trainingLogs: true,
        bomTable: true,
        appSetup: true,
      });

      // Fetch procedure revisions and training logs
      dispatch(getProcedureRevisionsForPart({ partId })).then(() => {
        setLoading((prev) => ({
          ...prev,
          revisionData: false,
          trainingLogs: false,
        }));
      });

      // Fetch BOM table
      dispatch(getBom({ partId, revisionId })).then(() => {
        setLoading((prev) => ({ ...prev, bomTable: false }));
      });

      // App setup data will be fetched on-demand when export button is clicked
      setLoading((prev) => ({ ...prev, appSetup: false }));
    }
  }, [dispatch, partId, revisionId]);

  return {
    // Data
    revisionData,
    trainingLogs,
    bomTable,

    // Loading states
    loading,
  };
};

export default usePreviewData;
